import { z } from 'zod';
import { knuthShuffle } from '@utils/common';
import { baseTaskSchema } from '../schema.base.js';
import { matchDropzoneInput } from './input.dropzone.js';

const baseTileSchema = z.object({
  correctAnswer: z.number().nullish().default(null),
  fixed: z.boolean().default(false)
});
const audioSchema = baseTileSchema.extend({
  type: z.literal("Audio"),
  title: z.string(),
  src: z.string()
});
const imageSchema = baseTileSchema.extend({
  type: z.literal("Image"),
  title: z.string(),
  src: z.string(),
  audioSrc: z.string().nullable().default(null),
  altText: z.string().nullable().default(null)
});
const textSchema = baseTileSchema.extend({
  type: z.literal("Text"),
  text: z.string()
});
const matchTaskSchema = baseTaskSchema.extend({
  taskType: z.literal("Match"),
  dropzones: z.array(matchDropzoneInput.schema).optional().default([]),
  items: z.array(z.discriminatedUnion("type", [audioSchema, imageSchema, textSchema])).optional().default([])
}).transform(({
  items,
  dropzones,
  ...rest
}) => {
  const tiles = items.map((it, index) => ({
    ...it,
    id: String(index)
  }));
  const content = dropzones.map((dropzone, index) => {
    //
    // STEP: Extract all tiles that the dropzone should matched based on
    // the backend configuration
    //
    const acceptedTiles = tiles.filter(it => it.correctAnswer === index);

    //
    // STEP: Ensure that all additional tiles that are identical to the
    // accepted tiles are also included in the entire set of allowed tiles
    // for the dropzone
    //
    const matchingTileIds = tiles.filter(it => {
      if (it.fixed) {
        return false;
      }
      return acceptedTiles.some(acceptedTile => {
        switch (true) {
          case it.type === "Audio" && acceptedTile?.type === "Audio":
            return it.src === acceptedTile?.src && it.title === acceptedTile?.title;
          case it.type === "Image" && acceptedTile?.type === "Image":
            return it.src === acceptedTile?.src && it.title === acceptedTile?.title;
          case it.type === "Text" && acceptedTile?.type === "Text":
            return it.text === acceptedTile?.text;
          default:
            return false;
        }
      });
    }).map(it => it.id);
    return matchDropzoneInput.postprocessSchemaInput(dropzone, {
      taskId: rest.id,
      index,
      acceptedTileIds: matchingTileIds
    });
  });
  return {
    ...rest,
    tiles: knuthShuffle(structuredClone(tiles)),
    content: knuthShuffle(structuredClone(content)),
    __orderedTiles: tiles,
    __orderedContent: content
  };
});

export { matchTaskSchema };
//# sourceMappingURL=_schema.js.map
